/**
 * PrintProductionTab.js — v6.32.145 Workstream F
 *
 * Super-Admin tabbed Print Production console.
 *
 * Renders the canonical 112-BLM matrix joined with GridFS presence +
 * v6.32.139 parity watermark state. Operator can:
 *   • filter by grade / modality / booklet / tier
 *   • see watermark + GridFS status badges per row
 *   • download an individual BLM (super-admin endpoint, audit-logged)
 *   • kick off the existing password-protected master archive build
 *     (re-uses /api/blm/print-master-archive/{start,status,download-token,
 *     download}; no new bulk infra added)
 *
 * READ-ONLY toward customer data. The only write is an audit_logs row
 * per per-file download (already enforced server-side).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { playSuccessChime, playFailureChime } from "../utils/maintenanceAudio";
import {
  Printer,
  Download,
  ShieldCheck,
  AlertTriangle,
  Loader2,
  Lock,
  Filter as FilterIcon,
  RefreshCw,
  Archive,
  FileCheck2,
  Upload,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrintProductionTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ grade: "", modality: "", booklet: "", tier: "", presentOnly: false });

  // Master archive job state
  const [archivePw, setArchivePw] = useState("");
  const [archiveJob, setArchiveJob] = useState(null);
  const [archiveBusy, setArchiveBusy] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/super-admin/print-production/list`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  // v6.34.4 — chime when the print-master archive job hits a terminal
  // state. Tracked via ref so we only fire once on the transition edge.
  const prevArchiveStatusRef = useRef(null);
  const fetchLatestArchive = async () => {
    try {
      const r = await axios.get(`${API}/blm/print-master-archive/status`);
      const job = r.data?.job || null;
      const prev = prevArchiveStatusRef.current;
      const curr = job?.status || job?.state || null;
      const terminal = ["complete", "completed", "ready", "failed", "error"];
      if (prev && !terminal.includes(prev) && curr && terminal.includes(curr)) {
        if (curr === "failed" || curr === "error") {
          playFailureChime();
        } else {
          playSuccessChime();
        }
      }
      prevArchiveStatusRef.current = curr;
      setArchiveJob(job);
    } catch {
      // not fatal — endpoint may legitimately have no job yet
    }
  };

  useEffect(() => {
    fetchList();
    fetchLatestArchive();
  }, []);

  const rows = data?.rows || [];
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filters.grade && r.grade !== filters.grade) return false;
      if (filters.modality && r.modality !== filters.modality) return false;
      if (filters.booklet && r.booklet_type !== filters.booklet) return false;
      if (filters.tier && r.tier !== filters.tier) return false;
      if (filters.presentOnly && !r.present_in_gridfs) return false;
      return true;
    });
  }, [rows, filters]);

  const downloadRow = async (filename) => {
    try {
      const res = await axios.get(
        `${API}/super-admin/print-production/file/${filename}`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Download failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const startArchive = async () => {
    if (archivePw.length < 8) {
      alert("Archive password must be at least 8 characters.");
      return;
    }
    setArchiveBusy(true);
    try {
      const r = await axios.post(`${API}/blm/print-master-archive/start`, {
        password: archivePw,
        edition: "2026.1",
      });
      setArchiveJob({ job_id: r.data?.job_id, status: "running", phase: "starting" });
      // poll for status every 3s for ~3 min
      let i = 0;
      const tick = async () => {
        if (i > 60) return;
        i += 1;
        await fetchLatestArchive();
        setTimeout(tick, 3000);
      };
      setTimeout(tick, 3000);
    } catch (e) {
      alert(`Archive start failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setArchiveBusy(false);
      setArchivePw("");
    }
  };

  if (loading) {
    return (
      <Card data-testid="print-production-loading">
        <CardContent className="py-12 flex items-center justify-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading the 112-BLM matrix…</span>
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card data-testid="print-production-error" className="border-rose-300">
        <CardContent className="py-6 text-rose-700">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="w-5 h-5" /> {error}
          </div>
          <Button size="sm" variant="outline" className="mt-4" onClick={fetchList}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const presentPct = Math.round((data.present_in_gridfs / data.total_expected) * 100);
  const wmPct = Math.round((data.watermarked_v1 / data.total_expected) * 100);

  return (
    <div className="space-y-6" data-testid="print-production-root">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-50 text-mosaic-brand flex items-center justify-center">
            <Printer className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-serif text-2xl text-mosaic-ink leading-tight">
              Print Production Console
            </h2>
            <p className="text-sm text-mosaic-ink-2">
              112-BLM matrix · GridFS presence · v6.32.139 parity watermark state · bulk archive.
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={fetchList} data-testid="print-production-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="print-production-summary">
        <SummaryCard
          label="Expected BLMs"
          value={data.total_expected}
          tone="brand"
          sub="7 grades × 4 modalities × 2 booklets × 2 tiers"
          testId="pp-summary-expected"
        />
        <SummaryCard
          label="Present in GridFS"
          value={`${data.present_in_gridfs} / ${data.total_expected}`}
          tone={data.present_in_gridfs === data.total_expected ? "emerald" : "amber"}
          sub={`${presentPct}% coverage`}
          testId="pp-summary-present"
        />
        <SummaryCard
          label="Watermarked v1"
          value={`${data.watermarked_v1} / ${data.total_expected}`}
          tone={data.is_complete ? "emerald" : "amber"}
          sub={`${wmPct}% parity-verified`}
          testId="pp-summary-watermarked"
        />
      </div>

      {/* Master archive panel */}
      <Card data-testid="print-production-archive-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-mosaic-ink-2 uppercase tracking-wider flex items-center gap-2">
            <Archive className="w-4 h-4" /> Master Archive (.zip, password-protected)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-mosaic-ink-2 mb-3">
            Builds a single password-protected ZIP of all 112 BLMs for the print shop.
            Re-uses the existing v6.32.x master-archive pipeline.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="flex items-center gap-2 flex-1">
              <Lock className="w-4 h-4 text-mosaic-ink-3 shrink-0" />
              <Input
                type="password"
                placeholder="Archive password (≥8 chars)"
                value={archivePw}
                onChange={(e) => setArchivePw(e.target.value)}
                data-testid="pp-archive-password-input"
              />
            </div>
            <Button
              onClick={startArchive}
              disabled={archiveBusy || archivePw.length < 8}
              data-testid="pp-archive-start-btn"
            >
              {archiveBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Archive className="w-4 h-4 mr-1.5" />}
              Start Build
            </Button>
          </div>
          {archiveJob && (
            <div className="mt-3 text-xs text-mosaic-ink-2 font-mono" data-testid="pp-archive-job-status">
              Job <b>{archiveJob.job_id}</b> · status: <b>{archiveJob.status || archiveJob.state}</b>
              {archiveJob.phase ? ` · phase: ${archiveJob.phase}` : ""}
              {archiveJob.progress != null ? ` · ${archiveJob.progress}%` : ""}
            </div>
          )}
        </CardContent>
      </Card>

      {/* v6.33 — Local-file parity diff tool. Operator drops a locally-held
          PDF; browser hashes it in-memory (zero bytes sent to the server)
          and matches it against every live GridFS SHA-256 in the matrix.
          Lets the operator self-verify parity without trusting any claim. */}
      <DiffFileCard rows={rows} />

      {/* Filters */}
      <Card data-testid="print-production-filters">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-mosaic-ink-2 mb-3">
            <FilterIcon className="w-4 h-4" /> Filters
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <SelectFilter label="Grade" value={filters.grade} options={["", ...data.grades]} onChange={(v) => setFilters({ ...filters, grade: v })} testId="pp-filter-grade" />
            <SelectFilter label="Modality" value={filters.modality} options={["", ...data.modalities]} onChange={(v) => setFilters({ ...filters, modality: v })} testId="pp-filter-modality" />
            <SelectFilter label="Booklet" value={filters.booklet} options={["", ...data.booklet_types]} onChange={(v) => setFilters({ ...filters, booklet: v })} testId="pp-filter-booklet" />
            <SelectFilter label="Tier" value={filters.tier} options={["", ...data.tiers]} onChange={(v) => setFilters({ ...filters, tier: v })} testId="pp-filter-tier" />
            <label className="flex items-center gap-2 text-mosaic-ink-2 ml-1">
              <input
                type="checkbox"
                checked={filters.presentOnly}
                onChange={(e) => setFilters({ ...filters, presentOnly: e.target.checked })}
                data-testid="pp-filter-present-only"
              />
              Present only
            </label>
          </div>
          <div className="text-xs text-mosaic-ink-3 mt-2">
            Showing {filteredRows.length} of {rows.length} expected BLMs.
          </div>
        </CardContent>
      </Card>

      {/* The matrix */}
      <Card data-testid="print-production-matrix-card">
        <CardContent className="pt-4 overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="print-production-matrix-table">
            <thead>
              <tr className="text-mosaic-ink-3 uppercase tracking-wider">
                <th className="text-left px-2 py-2 font-semibold">Filename</th>
                <th className="text-center px-2 py-2 font-semibold">GridFS</th>
                <th className="text-center px-2 py-2 font-semibold">Watermark v1</th>
                <th className="text-left px-2 py-2 font-semibold">Watermark hash</th>
                <th className="text-left px-2 py-2 font-semibold" title="SHA-256 of the bytes GridFS is serving right now">Live SHA-256</th>
                <th className="text-left px-2 py-2 font-semibold" title="Last upload to GridFS — the authoritative freshness timestamp">Last baked</th>
                <th className="text-right px-2 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.filename} className="border-t border-mosaic-border-soft" data-testid={`pp-row-${r.filename}`}>
                  <td className="px-2 py-1.5 font-mono">{r.filename}</td>
                  <td className="text-center px-2 py-1.5">
                    {r.present_in_gridfs ? (
                      <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200">present</Badge>
                    ) : (
                      <Badge className="bg-rose-50 text-rose-700 border-rose-200">missing</Badge>
                    )}
                  </td>
                  <td className="text-center px-2 py-1.5">
                    {r.watermarked_v1 ? (
                      <span className="inline-flex items-center gap-1 text-emerald-800">
                        <ShieldCheck className="w-3.5 h-3.5" /> v1
                      </span>
                    ) : (
                      <span className="text-amber-700">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-mosaic-ink-3">{r.composite_hash_short || "—"}</td>
                  <td
                    className="px-2 py-1.5 font-mono text-mosaic-ink-3 cursor-pointer hover:text-mosaic-ink-1"
                    title={r.live_sha256 ? `Click to copy full SHA-256:\n${r.live_sha256}` : ""}
                    onClick={() => r.live_sha256 && navigator.clipboard?.writeText(r.live_sha256)}
                    data-testid={`pp-live-sha-${r.filename}`}
                  >
                    {r.live_sha_short || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-mosaic-ink-3">{r.baked_at ? r.baked_at.slice(0, 19).replace("T", " ") : "—"}</td>
                  <td className="text-right px-2 py-1.5">
                    {r.present_in_gridfs ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadRow(r.filename)}
                        data-testid={`pp-download-${r.filename}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                    ) : (
                      <span className="text-xs text-mosaic-ink-3">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center px-4 py-6 text-mosaic-ink-3">
                    No rows match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-xs text-mosaic-ink-3 text-center pt-2">
        Each individual download writes an entry to <code>audit_logs</code>.
        The master archive is built via the existing print-master-archive pipeline.
      </p>
    </div>
  );
}

function SummaryCard({ label, value, sub, tone, testId }) {
  const toneMap = {
    brand: "bg-sky-50 text-mosaic-brand border-sky-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
  };
  return (
    <div className={`rounded-lg border ${toneMap[tone] || toneMap.brand} px-4 py-3`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-semibold leading-tight">{value}</div>
      {sub && <div className="text-[11px] mt-0.5 opacity-70">{sub}</div>}
    </div>
  );
}

function SelectFilter({ label, value, options, onChange, testId }) {
  return (
    <label className="block text-mosaic-ink-2">
      <span className="block uppercase tracking-wider text-[10px] mb-1 opacity-70">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-white border border-mosaic-border-soft rounded-md px-2 py-1.5 text-mosaic-ink"
      >
        {options.map((o) => (
          <option key={o || "any"} value={o}>{o || "Any"}</option>
        ))}
      </select>
    </label>
  );
}


/* ─────────────────────────────────────────────────────────────────────
 * DiffFileCard — self-verify parity without trusting any audit claim.
 *
 * The operator drops any local PDF; the browser hashes it with WebCrypto
 * SHA-256 in-memory (nothing is uploaded) and matches the resulting
 * hex against every live_sha256 in the matrix. If it matches, we report
 * the matching row. If it doesn't match any current live SHA, we flag
 * it as STALE / FOREIGN and show the closest filename match so the user
 * knows exactly what they're holding.
 * ───────────────────────────────────────────────────────────────────── */
function DiffFileCard({ rows }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const compute = async (f) => {
    setBusy(true);
    setResult(null);
    try {
      const buf = await f.arrayBuffer();
      const h = await crypto.subtle.digest("SHA-256", buf);
      const hex = Array.from(new Uint8Array(h)).map((b) => b.toString(16).padStart(2, "0")).join("");
      // Find matching row by live_sha256
      const match = (rows || []).find((r) => r.live_sha256 && r.live_sha256.toLowerCase() === hex);
      // Also look up what the file "claims" to be from its filename to flag staleness
      const claimedName = f.name.replace(/\s*\(\d+\)/, "").trim();
      const claimedRow = (rows || []).find((r) => r.filename === claimedName);
      setResult({
        hex,
        bytes: buf.byteLength,
        filename: f.name,
        match,
        claimedRow,
      });
    } catch (e) {
      setResult({ error: String(e) });
    } finally {
      setBusy(false);
    }
  };

  const onFile = (f) => {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) {
      setResult({ error: `Not a PDF: ${f.name}` });
      return;
    }
    setFile(f);
    compute(f);
  };

  const verdict = (() => {
    if (!result || result.error) return null;
    if (result.match) {
      return { tone: "ok", text: `✅ This file IS the current live copy of ${result.match.filename}.` };
    }
    if (result.claimedRow && result.claimedRow.live_sha256) {
      return {
        tone: "stale",
        text: `❌ This file is NOT the current ${result.claimedRow.filename}. Live SHA = ${result.claimedRow.live_sha_short}…; your file SHA = ${result.hex.slice(0, 12)}… → stale / outdated download.`,
      };
    }
    return { tone: "foreign", text: `⚠️ This SHA does not match any of the ${rows?.length || 0} live BLMs — foreign or heavily modified file.` };
  })();

  return (
    <Card data-testid="pp-diff-file-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileCheck2 className="w-4 h-4 text-mosaic-brand" /> Verify your local PDF against the live matrix
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-mosaic-ink-3">
          Drop any BLM PDF below. Your browser will hash it locally (nothing is uploaded) and tell you whether it is byte-identical to the file GridFS is serving right now.
        </p>
        <label
          className={`block cursor-pointer border-2 border-dashed rounded-lg px-4 py-6 text-center text-xs transition ${
            dragOver ? "border-mosaic-brand bg-sky-50" : "border-mosaic-border-soft bg-white hover:bg-mosaic-ink-1/5"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
          data-testid="pp-diff-file-dropzone"
        >
          <Upload className="w-5 h-5 mx-auto mb-2 text-mosaic-ink-3" />
          <div className="text-mosaic-ink-2">{file ? file.name : "Drop a PDF here or click to pick a file"}</div>
          <input
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
            data-testid="pp-diff-file-input"
          />
        </label>
        {busy && (
          <div className="flex items-center gap-2 text-xs text-mosaic-ink-3" data-testid="pp-diff-busy">
            <Loader2 className="w-4 h-4 animate-spin" /> Hashing locally…
          </div>
        )}
        {result && !busy && (
          <div className="text-xs space-y-2" data-testid="pp-diff-result">
            {result.error ? (
              <div className="text-rose-700 font-mono">{result.error}</div>
            ) : (
              <>
                <div className="font-mono text-mosaic-ink-2 break-all">
                  <span className="text-mosaic-ink-3">SHA-256: </span>{result.hex}
                </div>
                <div className="text-mosaic-ink-3">
                  {result.filename} · {(result.bytes / 1024 / 1024).toFixed(2)} MB
                </div>
                {verdict && (
                  <div
                    className={`rounded-md px-3 py-2 border ${
                      verdict.tone === "ok"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                        : verdict.tone === "stale"
                        ? "bg-rose-50 border-rose-200 text-rose-900"
                        : "bg-amber-50 border-amber-200 text-amber-900"
                    }`}
                    data-testid={`pp-diff-verdict-${verdict.tone}`}
                  >
                    {verdict.text}
                  </div>
                )}
                <button
                  type="button"
                  className="text-[11px] text-mosaic-ink-3 hover:text-mosaic-ink-1 inline-flex items-center gap-1"
                  onClick={() => { setFile(null); setResult(null); }}
                  data-testid="pp-diff-clear"
                >
                  <X className="w-3 h-3" /> clear
                </button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
